import { ModernPaginationProps } from '@moneyforward/ap-frontend-components';
import {
  FC,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface BaseInvoiceReportColumn {
  id: string;
  [key: string]: unknown;
}

type TriggerFindTargetReportProps<T extends BaseInvoiceReportColumn> = {
  children: React.ReactNode;
  data: T[];
  pagination: ModernPaginationProps;
  selectedIndex: number | undefined;
  setSelectedIndex: (index: number | undefined) => void;
  onSearch: (queryParams: { page?: number }) => void;
  isDataLoading: boolean;
};

type TriggerFindTargetReportContextType<T extends BaseInvoiceReportColumn> = {
  isTriggering: boolean;
  onTriggerFindTargetReport: (
    onSuccessFoundTargetReport: (report: T) => void,
    onErrorFoundTargetReport: () => void
  ) => void;
  onTriggerBackToPageOneIfNoData: () => void;
  selectedIndex: number | undefined;
  setSelectedIndex: (index: number | undefined) => void;
};

export function createTriggerFindTargetReportContext<
  T extends BaseInvoiceReportColumn
>() {
  const Context = createContext<TriggerFindTargetReportContextType<T> | null>(
    null
  );

  const Provider: FC<TriggerFindTargetReportProps<T>> = ({
    children,
    data,
    pagination,
    selectedIndex,
    setSelectedIndex,
    onSearch,
    isDataLoading,
  }) => {
    const [isTriggering, setIsTriggering] = useState<boolean>(false);
    const [isTriggeringBackToPageOne, setIsTriggeringBackToPageOne] =
      useState<boolean>(false);

    const onSuccessFoundTargetReportRef = useRef<((report: T) => void) | null>(
      null
    );
    const onErrorFoundTargetReportRef = useRef<(() => void) | null>(null);

    const onClearState = useCallback(() => {
      setIsTriggering(false);
      onSuccessFoundTargetReportRef.current = null;
      onErrorFoundTargetReportRef.current = null;
    }, []);

    const onTriggerFindTargetReport = useCallback(
      (
        onSuccessFoundTargetReport?: (report: T) => void,
        onErrorFoundTargetReport?: () => void
      ) => {
        if (isTriggering) return;
        onSuccessFoundTargetReportRef.current =
          onSuccessFoundTargetReport ?? null;
        onErrorFoundTargetReportRef.current = onErrorFoundTargetReport ?? null;
        setIsTriggering(true);
      },
      [isTriggering]
    );

    const onTriggerBackToPageOneIfNoData = useCallback(() => {
      setIsTriggeringBackToPageOne(true);
    }, []);

    /** Effect find target report */
    useEffect(() => {
      if (!isTriggering || selectedIndex === undefined || isDataLoading) return;

      if (!data.length && pagination.currentPage === 1) {
        onErrorFoundTargetReportRef.current?.();
        onClearState();
        return;
      }
      /** Trigger go back to page one if no data */
      if (!data.length && pagination.currentPage !== 1) {
        onTriggerBackToPageOneIfNoData();
        return;
      }

      if (data.length && data.length - 1 >= selectedIndex) {
        onSuccessFoundTargetReportRef.current?.(data[selectedIndex]!);
        onClearState();
        return;
      }

      onSuccessFoundTargetReportRef.current?.(data[0]!);
      onClearState();
    }, [
      isTriggering,
      data,
      selectedIndex,
      pagination,
      setSelectedIndex,
      onClearState,
      onTriggerBackToPageOneIfNoData,
      isDataLoading,
    ]);

    /** Effect go back to page one if no data */
    useEffect(() => {
      if (!isTriggeringBackToPageOne) return;
      if (!data.length && pagination.currentPage !== 1) {
        onSearch({ page: 1 });
        setSelectedIndex(0);
        return;
      }
      setIsTriggeringBackToPageOne(false);
    }, [
      data,
      pagination.currentPage,
      onSearch,
      setSelectedIndex,
      isTriggeringBackToPageOne,
    ]);

    const value = useMemo(
      () => ({
        isTriggering,
        onTriggerFindTargetReport,
        onTriggerBackToPageOneIfNoData,
        selectedIndex,
        setSelectedIndex,
      }),
      [
        isTriggering,
        onTriggerFindTargetReport,
        onTriggerBackToPageOneIfNoData,
        selectedIndex,
        setSelectedIndex,
      ]
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
  };

  const useTriggerFindTargetReport = () => {
    const context = useContext(Context);

    if (!context) {
      throw new Error(
        'useTriggerFindTargetReport must be used within a TriggerFindTargetReportProvider'
      );
    }

    return context;
  };

  return { Provider, useTriggerFindTargetReport };
}
